import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import AdminLoginGate from '../components/AdminLoginGate';
import AdminTabs from '../components/AdminTabs';
import { API, authHeaders, useAdminAuth } from '../lib/adminAuth';
import { AlertTriangle, ShieldCheck, Users, Flame, Heart, Candy, UserCog, Mail } from 'lucide-react';

// Role-aware list of admin areas with a short pitch and a stat the dashboard
// will populate asynchronously. Keep the paths in sync with App.js routes and
// the labels in sync with AdminTabs.
const AREAS = [
  {
    key: 'bonfire', path: '/admin/bonfire-signups', title: 'Bonfire List',
    blurb: 'Launch-list signups for Bonfire Night 2026 — manage subscribers and export CSV.',
    roles: ['superadmin'], Icon: Flame,
    endpoint: '/admin/bonfire-signups', countLabel: 'signups',
  },
  {
    key: 'contact', path: '/admin/contact-enquiries', title: 'Contact Inbox',
    blurb: 'Every message submitted via the public Contact page — including any Maileroo failed to deliver.',
    roles: ['superadmin', 'donations'], Icon: Mail,
    endpoint: '/admin/contact-enquiries', countLabel: 'messages',
  },
  {
    key: 'donations', path: '/admin/donation-requests', title: 'Donation Requests',
    blurb: 'Review funding applications from local charities & community groups. Toggle the public page on/off.',
    roles: ['superadmin', 'donations'], Icon: Heart,
    endpoint: '/admin/donation-requests', countLabel: 'applications',
  },
  {
    key: 'sweet-jar', path: '/admin/sweet-jar', title: 'Sweet Jar Competition',
    blurb: 'Heritage Weekend entries, winner-finder, and the printable QR code.',
    roles: ['superadmin', 'donations'], Icon: Candy,
    endpoint: '/admin/sweet-jar/guesses', countLabel: 'guesses',
    countFromKey: 'total',
  },
  {
    key: 'users', path: '/admin/users', title: 'Admin Users',
    blurb: 'Create or remove admin accounts, reset passwords, help someone back in after a lost 2FA device.',
    roles: ['superadmin'], Icon: Users,
    endpoint: '/admin/users', countLabel: 'users',
  },
  {
    key: 'profile', path: '/admin/profile', title: 'My Profile',
    blurb: 'Change your password, turn on authenticator-app two-factor sign-in.',
    roles: ['superadmin', 'donations'], Icon: UserCog,
  },
];

function useAreaCounts(token, role) {
  // Fire-and-forget counts for each area tile. Soft-fails per endpoint so a
  // broken sub-area doesn't bring down the whole dashboard.
  //
  // IMPORTANT: this effect runs ONCE per (token, role) pair. A previous
  // iteration passed `areas` as the dep and recomputed `areas` on every
  // render, which caused an infinite fetch loop (every setCounts triggered
  // a re-render -> new areas array identity -> effect reran). Keying on
  // primitives avoids the loop entirely.
  const [counts, setCounts] = useState({});
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const run = async () => {
      const areas = AREAS.filter((a) => a.endpoint && a.roles.includes(role));
      const results = await Promise.allSettled(
        areas.map(async (a) => {
          const { data } = await axios.get(`${API}${a.endpoint}`, { headers: authHeaders(token) });
          const n = a.countFromKey
            ? Number(data?.[a.countFromKey] ?? NaN)
            : (Array.isArray(data) ? data.length : NaN);
          return [a.key, Number.isFinite(n) ? n : null];
        }),
      );
      if (cancelled) return;
      const next = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          const [k, v] = r.value;
          next[k] = v;
        }
      });
      setCounts(next);
    };
    run();
    return () => { cancelled = true; };
  }, [token, role]);
  return counts;
}

function Dashboard({ token, me, onLogout }) {
  const visible = useMemo(() => AREAS.filter((a) => a.roles.includes(me.role)), [me.role]);
  const counts = useAreaCounts(token, me.role);

  return (
    <div className="bg-[#F4F4F5] min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="admin-home-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">Admin Dashboard</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]">Welcome back, {me.username}</h1>
          <p className="text-sm text-[#52525B] mt-2">
            One sign-in covers every admin area below. Role: <span className="uppercase tracking-wider font-bold text-[#0A0A0A]">{me.role}</span>.
          </p>
        </div>

        {/* 2FA nudge — the admin dashboard holds personal data (donation bank
            details, bonfire signups, sweet-jar entries) so we strongly encourage
            every account to enable TOTP. */}
        {!me.totp_enabled && (
          <div className="bg-[#0A0A0A] text-white p-5 sm:p-6 border-l-4 border-[#F0A500] mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="admin-home-2fa-nudge">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#F0A500] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-[#F0A500] mb-1">Two-factor sign-in is off</div>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Add a second layer of protection using an authenticator app (Google Authenticator, Authy, 1Password, Microsoft Authenticator). Takes 60 seconds &mdash; strongly recommended.
                </p>
              </div>
            </div>
            <Link
              to="/admin/profile"
              data-testid="admin-home-enable-2fa-btn"
              className="px-5 py-2.5 bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs whitespace-nowrap text-center"
            >
              Enable 2FA now &rarr;
            </Link>
          </div>
        )}

        {me.totp_enabled && (
          <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-10 flex items-center gap-3" data-testid="admin-home-2fa-ok">
            <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-sm text-emerald-900">
              Two-factor sign-in is <strong>on</strong>. Great &mdash; your account is protected.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((area) => {
            const { Icon } = area;
            const count = counts[area.key];
            return (
              <Link
                key={area.key}
                to={area.path}
                data-testid={`admin-home-tile-${area.key}`}
                className="group bg-white border border-zinc-200 hover:border-[#F0A500] p-6 transition-colors flex flex-col"
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className="w-6 h-6 text-[#F0A500]" />
                  {typeof count === 'number' && (
                    <span className="text-xs uppercase tracking-widest font-bold text-[#52525B]">
                      <span className="text-xl text-[#0A0A0A] mr-1.5 align-middle">{count}</span>{area.countLabel}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-[#0A0A0A] mb-2 group-hover:text-[#F0A500] transition-colors">
                  {area.title}
                </h3>
                <p className="text-sm text-[#52525B] leading-relaxed flex-1">{area.blurb}</p>
                <span className="text-xs uppercase tracking-widest font-bold text-[#F0A500] mt-4">
                  Open &rarr;
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function AdminHomePage() {
  const { token, me, checking, loginSuccess, logout } = useAdminAuth();
  if (checking) return <div className="min-h-[70vh] flex items-center justify-center text-zinc-500">Checking session…</div>;
  if (!token || !me) {
    return (
      <div data-testid="admin-home-page">
        <SEO title="Admin Sign-in" description="Restricted admin area." path="/admin" noIndex />
        <AdminLoginGate title="Admin Dashboard" subtitle="Sign in once to manage every admin area." onLoggedIn={loginSuccess} />
      </div>
    );
  }
  return (
    <div data-testid="admin-home-page">
      <SEO title="Admin Dashboard" description="Restricted admin area." path="/admin" noIndex />
      <AdminTabs current="home" me={me} onLogout={logout} />
      <Dashboard token={token} me={me} onLogout={logout} />
    </div>
  );
}
